import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  groupWaiverResults, resultLine, pickRunGroup, bidCount,
  type WaiverResultRow, type PlayerGroup,
} from '@/lib/nflWaiverResults';

/**
 * SAB-1208: one waiver claim, in full.
 *
 * The activity feed writes a row only for the AWARDED claim and says "Waiver claim won".
 * Everything a manager actually wants to know -- who else bid, how much, who lost on the
 * tie rule -- has been in nfl_waiver_results since SAB-1038, reachable only through a text
 * toggle on the Players page. This is the same data, where the user already is.
 *
 * ── WHY IT CALLS THE RPC SCOPED, NOT FILTERED ───────────────────────────────
 *
 * The obvious implementation -- call nfl_waiver_results(league, 200) and filter to the
 * player in JS -- is wrong, and quietly. The function's LIMIT applies to the flat claim
 * list before grouping, so:
 *
 *   * a claim outside the 200 most recent returns NOTHING. Measured on staging: A.J. Brown's
 *     4-row contested group sits at ranks 292-299 of 325, so the filtered call returns 0
 *     rows and the scoped call returns 4. The modal would have opened EMPTY on a claim the
 *     feed had just advertised.
 *   * the boundary can fall MID-GROUP, returning a winner without some losers -- rendering
 *     a contested claim as uncontested while every number on screen stays correct.
 *
 * p_player_id (SAB-1208) narrows server-side, so the limit bounds one player's own history
 * instead of the league's and can no longer cut a group in half.
 *
 * ── WHY IT SHARES resultLine() WITH THE PANEL ───────────────────────────────
 *
 * Two surfaces showing the same claim is how they come to word it differently. The panel
 * and this modal both render through resultLine(), so "Outbid -- bid $18, winning bid $29"
 * is one string built in one place. If the wording is wrong it is wrong in both, which is
 * the failure mode you can actually find.
 */
export default function NFLWaiverClaimModal({
  leagueId, playerId, runDate, open, onOpenChange,
}: {
  leagueId: string | null;
  playerId: string | null;
  /** From the feed row's metadata. Null for rows written before SAB-1208. */
  runDate: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [rows, setRows] = useState<WaiverResultRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!leagueId || !playerId) return;
    setLoading(true);
    setError(null);
    setRows(null);
    try {
      const { data, error: rpcErr } = await (supabase as unknown as {
        rpc: (fn: string, args: unknown) => Promise<{ data: unknown; error: { message: string } | null }>;
      }).rpc('nfl_waiver_results', {
        p_league_id: leagueId,
        p_limit: 200,
        p_player_id: playerId,
      });
      if (rpcErr) {
        // Same distinction the panel draws: a refusal is not an empty log, and rendering
        // "no detail" for a member who was denied misreports their league.
        setError(/not_a_league_member/.test(rpcErr.message)
          ? 'Waiver results are visible to league members only.'
          : 'This claim is unavailable right now.');
        return;
      }
      setRows((data ?? []) as WaiverResultRow[]);
    } catch {
      setError('This claim is unavailable right now.');
    } finally {
      setLoading(false);
    }
  }, [leagueId, playerId]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const group: PlayerGroup | null = useMemo(() => {
    if (!rows) return null;
    const run = pickRunGroup(groupWaiverResults(rows), runDate);
    return run?.players[0] ?? null;
  }, [rows, runDate]);

  if (!open || !playerId) return null;

  const close = () => onOpenChange(false);
  const count = group ? bidCount(group) : 0;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      onClick={close}
      data-testid="waiver-claim-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div
        className={cn(
          'relative z-[70] w-full sm:max-w-[480px] bg-card border border-border rounded-t-2xl sm:rounded-2xl',
          'max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden',
          'animate-in slide-in-from-bottom sm:fade-in sm:zoom-in-95 duration-200',
        )}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              Waiver claim
            </p>
            <p className="mt-0.5 flex items-baseline gap-1.5 min-w-0">
              <span className="min-w-0 truncate text-sm font-display font-bold text-foreground">
                {group?.playerName ?? 'Claim detail'}
              </span>
              {group?.playerPosition && (
                <span className="shrink-0 text-[10px] text-muted-foreground">{group.playerPosition}</span>
              )}
            </p>
            {/* The run, named. Two claims on one player in different runs are different
                stories, and the header is what tells them apart. */}
            {group && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {group.rows[0]?.run_date}
                {count > 1 && <> · {count} bids</>}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="shrink-0 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-muted/40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-2 overflow-y-auto">
          {loading && (
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </p>
          )}
          {error && <p data-testid="waiver-claim-error" className="text-xs text-muted-foreground">{error}</p>}

          {/* An absent claim is STATED. A modal that opens on nothing and shows an empty
              card reads as a loading bug, and sends the user to support rather than on. */}
          {!loading && !error && !group && (
            <p data-testid="waiver-claim-empty" className="text-xs text-muted-foreground">
              This claim is no longer in the waiver log.
            </p>
          )}

          {group?.rows.map(row => (
            <div
              key={row.id}
              data-testid="waiver-claim-row"
              className={cn(
                'flex flex-col sm:flex-row sm:items-baseline sm:gap-2 text-[11px] rounded-md px-2 py-1.5',
                row.status === 'awarded' ? 'bg-emerald-500/10' : 'bg-muted/30',
              )}
            >
              {/* Same flex discipline as the panel (SAB-1005): exactly one column claims
                  the space, both text columns carry min-w-0, and they STACK on narrow
                  rather than compete for one row. */}
              <span className={cn(
                'min-w-0 truncate',
                row.status === 'awarded' ? 'text-emerald-300 font-medium' : 'text-muted-foreground',
              )}>
                {row.team_name}
              </span>
              <span className="min-w-0 sm:flex-1 truncate text-muted-foreground sm:text-right">
                {resultLine(row)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
